import React, { useRef } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { ContextMenu, ContextMenuTrigger, MenuItem, SubMenu } from 'react-contextmenu';
import getFolderAbility from './ability';
import Icon from '../Icons';
import { useDirectoryNodes } from './DirectoryNodesContext';

const FolderActionButtons = ({
  handleButtonClick,
  parentId,
  folder,
  ability,
  hasPermissionsToAddFiles,
  handleOpenCenterModal,
  handleFirstNodeRequest,
  handleMoveFolder,
  directoryNodes
}) => {
  const contextTrigger = useRef(null);
  const { rrhh } = useDirectoryNodes();
  if (!folder) return null;
  const permissions = getFolderAbility(camelCaseRecursive(folder), ability);
  const canMoveFile = ability.can('update_location', 'Document');
  const toggleMenu = e => {
    if (contextTrigger.current) {
      contextTrigger.current.handleContextClick(e);
    }
  };

  const folderButtons = [];
  if (hasPermissionsToAddFiles && rrhh) {
    folderButtons.push({
      key: 'add-file',
      icon: 'document',
      title: 'Agregar archivo',
      props: {
        onClick: () =>
          handleOpenCenterModal(folder.id, folder.name, 'file', () => handleFirstNodeRequest(folder.id, true)),
        className: 'text-capitalize'
      }
    });
  }

  if (permissions[5]) {
    folderButtons.push({
      key: 'download',
      icon: 'cloud-download',
      title: 'Descargar plantilla de actualización',
      props: {
        onClick: () => handleButtonClick(folder, 'download-folder', parentId),
        className: 'text-capitalize'
      }
    });
  }

  if (hasPermissionsToAddFiles && rrhh) {
    folderButtons.push({
      key: 'add-folder',
      icon: 'folder-plus',
      title: 'Crear Carpeta',
      props: {
        onClick: () =>
          handleOpenCenterModal(folder.id, folder.name, 'folder', () => handleFirstNodeRequest(folder.id, true)),
        className: 'text-capitalize'
      }
    });
  }

  if (folder.destroyable && permissions[2] && rrhh) {
    folderButtons.push({
      key: 'delete',
      icon: 'folder-minus',
      title: 'Eliminar carpeta',
      props: {
        onClick: () => handleButtonClick(folder, 'remove-folder', parentId),
        className: 'text-danger text-capitalize'
      }
    });
  }

  if (permissions[1] && rrhh) {
    folderButtons.push({
      key: 'export',
      icon: 'folder-download',
      title: 'Exportar Carpeta',
      props: {
        onClick: () => handleButtonClick(folder, 'export-folder', parentId),
        className: 'text-capitalize'
      }
    });
  }

  if (permissions[6] && parentId !== undefined && rrhh) {
    folderButtons.push({
      key: 'update',
      icon: 'pencil',
      title: 'Cambiar nombre',
      props: {
        onClick: () => handleButtonClick(folder, 'change-name', parentId),
        className: 'text-capitalize'
      }
    });
  }

  const createSubFolder = (directoryNode, folderId) => {
    const { id: directoryNodeId } = directoryNode;

    return (
      <div key={`directory-${directoryNode.id}-folder-${folderId}`}>
        {directoryNodeId !== folderId && directoryNode.hasChildren ? (
          <SubMenu
            title={
              <>
                <span>{directoryNode.name}</span>
                <Icon className="float-right mt-1" height={15} icon="chevron-forward" />
              </>
            }
            onClick={() => handleMoveFolder(directoryNodeId, folderId)}
          >
            {directoryNode.hasChildren &&
              directoryNode?.children?.map(children =>
                children.id !== folderId ? (
                  createSubFolder(children, folderId)
                ) : (
                  <MenuItem key={folderId} disabled>
                    {children.name}
                  </MenuItem>
                )
              )}
            {!directoryNode.hasChildren && <></>}
          </SubMenu>
        ) : (
          <MenuItem onClick={() => handleMoveFolder(directoryNodeId, folderId)}>{directoryNode.name}</MenuItem>
        )}
      </div>
    );
  };

  if (canMoveFile && folder?.dependFrom?.id && folderButtons.length > 0 && rrhh) {
    const moveButton = {
      key: 'move',
      icon: 'share',
      title: 'Mover carpeta',
      isContextMenu: true,
      folderId: folder.id,
      directoryNodes,
      createSubFolder,
      handleMoveFolder
    };

    folderButtons.splice(folderButtons.length - 1, 0, moveButton);
  }

  return (
    <>
      {folderButtons.map(button => {
        if (button.isContextMenu) {
          return (
            <React.Fragment key={button.key}>
              <ContextMenuTrigger id={`folder-${button.folderId}`} ref={contextTrigger} onShow={toggleMenu}>
                <OverlayTrigger
                  placement="left"
                  overlay={<Tooltip id={`tooltip-${button.key}`}>{button.title}</Tooltip>}
                >
                  <Button variant="outline" size="sm" onClick={toggleMenu}>
                    <Icon icon={button.icon} width="20px" height="20px" />
                  </Button>
                </OverlayTrigger>
                <ContextMenu id={`folder-${button.folderId}`}>
                  {button.directoryNodes?.map(directoryNode => button.createSubFolder(directoryNode, button.folderId))}
                </ContextMenu>
              </ContextMenuTrigger>
            </React.Fragment>
          );
        }

        return (
          <OverlayTrigger
            key={button.key}
            placement="left"
            overlay={<Tooltip id={`tooltip-${button.key}`}>{button.title}</Tooltip>}
          >
            <Button key={button.key} variant="outline" size="sm" className="mr-2" {...button.props}>
              <Icon icon={button.icon} width="20px" height="20px" />
            </Button>
          </OverlayTrigger>
        );
      })}
    </>
  );
};

export default FolderActionButtons;
